/**
 * Đọc an toàn nội dung `RecordDto.data`. Dữ liệu cũ có thể thiếu trường hoặc sai kiểu (nhiều
 * đợt nâng cấp, nhiều người sửa) — đọc phải CHỊU ĐƯỢC, không ném lỗi làm trắng cả màn hình.
 */
export type RecordData = Record<string, unknown>;

export function readString(data: RecordData, key: string, fallback = ''): string {
  const value = data[key];
  if (typeof value === 'string') return value;
  return typeof value === 'number' ? String(value) : fallback;
}

export function readOptionalString(data: RecordData, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

export function readNumber(data: RecordData, key: string): number | null {
  const value = data[key];
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function readStringList(data: RecordData, key: string): string[] {
  const value = data[key];
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

/** Cặp [tên, phần trăm] — quốc gia, thiết bị. Dòng sai dạng bị bỏ qua. */
export type NamedShare = [name: string, percent: number];

export function readNamedShares(data: RecordData, key: string): NamedShare[] {
  const value = data[key];
  if (!Array.isArray(value)) return [];
  const shares: NamedShare[] = [];
  for (const row of value) {
    if (!Array.isArray(row)) continue;
    const [name, percent] = row as unknown[];
    if (typeof name === 'string' && typeof percent === 'number' && Number.isFinite(percent)) {
      shares.push([name, percent]);
    }
  }
  return shares;
}

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Ghi đè lên dữ liệu đang lưu CHỈ những khoá THẬT SỰ ĐỔI.
 *
 *   stored   — `data` đang nằm trong CSDL (có thể thiếu khoá, có khoá lạ, có dạng cũ)
 *   previous — mô hình ĐỌC từ `stored` rồi ghi lại đầy đủ
 *   next     — mô hình SAU KHI người dùng sửa, ghi lại đầy đủ
 *
 * Khoá nào `previous` và `next` giống nhau nghĩa là người dùng không đụng tới → giữ NGUYÊN VĂN
 * cái đang lưu (kể cả «vắng mặt»). Không có bước này, sửa một ô CTR sẽ âm thầm thêm `st`,
 * `thumb2: null`… vào bản ghi, và cắt danh sách quốc gia của dữ liệu cũ — kiểm trên dữ liệu thật
 * 21/09/2026: 188/3.722 video bị đổi chỉ vì đọc-rồi-ghi. Khoá lạ (vd. `rpmNA`) luôn được giữ.
 */
export function applyChanges(
  stored: RecordData,
  previous: RecordData,
  next: RecordData,
): RecordData {
  const result: RecordData = { ...stored };
  for (const key of new Set([...Object.keys(previous), ...Object.keys(next)])) {
    if (sameJson(previous[key], next[key])) continue;
    if (next[key] === undefined) delete result[key];
    else result[key] = next[key];
  }
  return result;
}

/** Bỏ khoá mang giá trị `undefined` trước khi gửi — «vắng mặt» phải thật sự vắng mặt trong JSON. */
export function compactData<T extends RecordData>(data: T): RecordData {
  return Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined));
}
